import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, 
  Tooltip, ResponsiveContainer, Legend, AreaChart, Area, CartesianGrid 
} from 'recharts';
import { 
  Calendar, ChevronDown, ChevronLeft, ChevronRight, Clock, Globe, 
  Monitor, Smartphone, Users, Activity, Search, Loader, Filter, 
  BarChart2, PieChart as PieChartIcon, LineChart as LineChartIcon,
  TrendingUp, Download, RefreshCw, Settings, ArrowUp, ArrowDown
} from 'lucide-react';

const AnalyticsDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '2025-03-30', end: '2025-04-06' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    device: null,
    country: null,
    userType: null,
    timeRange: '7d'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [chartType, setChartType] = useState('bar'); // 'bar', 'line', 'area'
  
  const colors = {
    desktop: '#4F46E5', // Indigo
    mobile: '#7C3AED', // Purple
    tablet: '#9333EA', // Purple
    US: '#2563EB', // Blue
    KE: '#059669', // Green
    GB: '#DC2626', // Red
    other: '#9333EA', // Purple
    superuser: '#D97706', // Amber
    admin: '#10B981', // Emerald
    user: '#3B82F6', // Blue
    guest: '#6B7280', // Gray
  };
  
  // Mock data for time series - in a real app this would come from the API
  const timeSeriesData = [
    { date: '2025-03-30', visitors: 10, pageViews: 15, avgTime: 45.2 },
    { date: '2025-03-31', visitors: 15, pageViews: 22, avgTime: 48.7 },
    { date: '2025-04-01', visitors: 22, pageViews: 35, avgTime: 52.1 },
    { date: '2025-04-02', visitors: 18, pageViews: 28, avgTime: 50.3 },
    { date: '2025-04-03', visitors: 25, pageViews: 42, avgTime: 55.6 },
    { date: '2025-04-04', visitors: 30, pageViews: 50, avgTime: 58.2 },
    { date: '2025-04-05', visitors: 28, pageViews: 45, avgTime: 56.8 },
    { date: '2025-04-06', visitors: 35, pageViews: 60, avgTime: 60.1 },
  ];

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        // Build query params based on filters
        const params = new URLSearchParams({
          start: dateRange.start,
          end: dateRange.end,
          ...(filters.device && { device: filters.device }),
          ...(filters.country && { country: filters.country }),
          ...(filters.userType && { user_type: filters.userType }),
        });

        const response = await fetch(`/api/analytics/summary?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        setAnalyticsData(data);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to fetch analytics data');
        console.error('Error fetching analytics data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAnalytics();
  }, [dateRange, filters]);

  const handleDateChange = (startDate, endDate) => {
    setDateRange({ start: startDate, end: endDate });
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value === 'all' ? null : value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const resetFilters = () => {
    setFilters({
      device: null,
      country: null,
      userType: null,
      timeRange: '7d'
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Filter activity data based on search term and filters
  const filteredActivity = analyticsData?.sample_activity?.filter(item => {
    // Search term filtering
    const searchMatch = 
      item.page_url.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.page_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.user.username && item.user.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
      item.device.browser.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.location.country_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.location.city.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Device filter
    const deviceMatch = !filters.device || item.device.device_type === filters.device;
    
    // Country filter
    const countryMatch = !filters.country || item.location.country_code === filters.country;
    
    // User type filter
    const userTypeMatch = 
      !filters.userType || 
      (filters.userType === 'guest' && !item.user.username) ||
      (filters.userType === 'user' && item.user.username && item.user.role === 'user') ||
      (filters.userType === 'admin' && item.user.username && item.user.role === 'admin') ||
      (filters.userType === 'superuser' && item.user.username && item.user.role === 'superuser');
    
    return searchMatch && deviceMatch && countryMatch && userTypeMatch;
  }) || [];

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredActivity.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredActivity.length / itemsPerPage);

  // Process most visited pages for the progress bar chart
  const processMostVisitedPages = () => {
    if (!analyticsData?.most_visited_pages) return [];
    
    const maxVisits = Math.max(...analyticsData.most_visited_pages.map(p => p.visits));
    
    return analyticsData.most_visited_pages.map(page => ({
      ...page,
      fillPercent: (page.visits / maxVisits) * 100,
      truncatedUrl: page.url.length > 30 ? `${page.url.substring(0, 27)}...` : page.url
    }));
  };

  // Loading states
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen w-full bg-gray-900 text-white">
        <Loader className="animate-spin w-12 h-12 text-indigo-500 mb-4" />
        <h3 className="text-xl font-medium">Loading Analytics Data...</h3>
      </div>
    );
  }
  
  // Error states
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen w-full bg-gray-900 text-white">
        <div className="bg-red-900/30 border border-red-600 rounded-lg p-6 max-w-lg">
          <h3 className="text-xl font-medium text-red-400 mb-2">Error Loading Analytics</h3>
          <p className="text-gray-300">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-red-700 hover:bg-red-600 rounded-md transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  // If no data
  if (!analyticsData) {
    return (
      <div className="flex flex-col items-center justify-center h-screen w-full bg-gray-900 text-white">
        <h3 className="text-xl font-medium">No data available</h3>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 p-4 shadow-md">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center mb-4 md:mb-0">
            <h1 className="text-2xl font-bold text-white mr-4">Analytics Dashboard</h1>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center px-3 py-1.5 rounded-md text-sm ${showFilters ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
              >
                <Filter className="h-4 w-4 mr-1" />
                Filters
              </button>
              <button 
                onClick={resetFilters}
                className="flex items-center px-3 py-1.5 rounded-md text-sm bg-gray-700 text-gray-300 hover:bg-gray-600"
              >
                Reset
              </button>
            </div>
          </div>
          
          {/* Date Selector */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 bg-gray-700 rounded-lg p-2 text-sm">
              <Calendar className="h-4 w-4 text-indigo-400" />
              <span>{dateRange.start}</span>
              <span>to</span>
              <span>{dateRange.end}</span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
            <button className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg">
              <RefreshCw className="h-4 w-4 text-gray-300" />
            </button>
            <button className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg">
              <Download className="h-4 w-4 text-gray-300" />
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="container mx-auto mt-4 bg-gray-700/50 rounded-lg p-4 border border-gray-600">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Device Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Device Type</label>
                <select
                  value={filters.device || 'all'}
                  onChange={(e) => handleFilterChange('device', e.target.value)}
                  className="w-full bg-gray-800 border border-gray-600 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Devices</option>
                  <option value="desktop">Desktop</option>
                  <option value="mobile">Mobile</option>
                  <option value="tablet">Tablet</option>
                </select>
              </div>

              {/* Country Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Country</label>
                <select
                  value={filters.country || 'all'}
                  onChange={(e) => handleFilterChange('country', e.target.value)}
                  className="w-full bg-gray-800 border border-gray-600 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Countries</option>
                  {analyticsData.countries.map((country, index) => (
                    <option key={index} value={country.code}>{country.code}</option>
                  ))}
                </select>
              </div>

              {/* User Type Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">User Type</label>
                <select
                  value={filters.userType || 'all'}
                  onChange={(e) => handleFilterChange('userType', e.target.value)}
                  className="w-full bg-gray-800 border border-gray-600 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Users</option>
                  <option value="guest">Guests</option>
                  <option value="user">Regular Users</option>
                  <option value="admin">Admins</option>
                  <option value="superuser">Superusers</option>
                </select>
              </div>

              {/* Time Range Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Time Range</label>
                <select
                  value={filters.timeRange}
                  onChange={(e) => handleFilterChange('timeRange', e.target.value)}
                  className="w-full bg-gray-800 border border-gray-600 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="24h">Last 24 hours</option>
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </header>
      
      {/* Main Content */}
      <main className="container mx-auto p-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Unique Visitors */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-lg hover:border-indigo-500 transition-colors">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-gray-400 text-sm font-medium">Unique Visitors</h3>
                <p className="text-3xl font-bold text-white">{analyticsData.unique_visitors}</p>
              </div>
              <div className="p-2 bg-indigo-900/30 rounded-lg">
                <Users className="h-6 w-6 text-indigo-400" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs">
              <span className="text-green-400 flex items-center">
                <ArrowUp className="h-3 w-3 mr-1" /> 12%
              </span>
              <span className="text-gray-500 ml-1">vs previous period</span>
            </div>
          </div>
          
          {/* Avg Time on Page */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-lg hover:border-purple-500 transition-colors">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-gray-400 text-sm font-medium">Avg. Time on Page</h3>
                <p className="text-3xl font-bold text-white">{analyticsData.average_time_on_page.toFixed(1)}s</p>
              </div>
              <div className="p-2 bg-purple-900/30 rounded-lg">
                <Clock className="h-6 w-6 text-purple-400" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs">
              <span className="text-green-400 flex items-center">
                <ArrowUp className="h-3 w-3 mr-1" /> 8%
              </span>
              <span className="text-gray-500 ml-1">vs previous period</span>
            </div>
          </div>
          
          {/* Page Views */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-lg hover:border-blue-500 transition-colors">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-gray-400 text-sm font-medium">Total Page Views</h3>
                <p className="text-3xl font-bold text-white">
                  {analyticsData.most_visited_pages.reduce((sum, page) => sum + page.visits, 0)}
                </p>
              </div>
              <div className="p-2 bg-blue-900/30 rounded-lg">
                <Activity className="h-6 w-6 text-blue-400" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs">
              <span className="text-green-400 flex items-center">
                <ArrowUp className="h-3 w-3 mr-1" /> 15%
              </span>
              <span className="text-gray-500 ml-1">vs previous period</span>
            </div>
          </div>
          
          {/* Bounce Rate */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-lg hover:border-green-500 transition-colors">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-gray-400 text-sm font-medium">Bounce Rate</h3>
                <p className="text-3xl font-bold text-white">32%</p>
              </div>
              <div className="p-2 bg-green-900/30 rounded-lg">
                <TrendingUp className="h-6 w-6 text-green-400" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-xs">
              <span className="text-red-400 flex items-center">
                <ArrowDown className="h-3 w-3 mr-1" /> 5%
              </span>
              <span className="text-gray-500 ml-1">vs previous period</span>
            </div>
          </div>
        </div>
        
        {/* Time Series Chart */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-lg mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Traffic Overview</h3>
            <div className="flex space-x-2">
              <button 
                onClick={() => setChartType('bar')} 
                className={`p-1.5 rounded ${chartType === 'bar' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
              >
                <BarChart2 className="h-4 w-4" />
              </button>
              <button 
                onClick={() => setChartType('line')} 
                className={`p-1.5 rounded ${chartType === 'line' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
              >
                <LineChartIcon className="h-4 w-4" />
              </button>
              <button 
                onClick={() => setChartType('area')} 
                className={`p-1.5 rounded ${chartType === 'area' ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
              >
                <PieChartIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'bar' ? (
                <BarChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', borderRadius: '0.375rem' }}
                    labelStyle={{ color: '#F9FAFB' }}
                  />
                  <Legend 
                    formatter={(value) => <span className="text-gray-300">{value}</span>}
                  />
                  <Bar dataKey="visitors" fill="#4F46E5" name="Unique Visitors" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pageViews" fill="#7C3AED" name="Page Views" radius={[4, 4, 0, 0]} />
                </BarChart>
              ) : chartType === 'line' ? (
                <LineChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', borderRadius: '0.375rem' }}
                    labelStyle={{ color: '#F9FAFB' }}
                  />
                  <Legend 
                    formatter={(value) => <span className="text-gray-300">{value}</span>}
                  />
                  <Line type="monotone" dataKey="visitors" stroke="#4F46E5" name="Unique Visitors" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="pageViews" stroke="#7C3AED" name="Page Views" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              ) : (
                <AreaChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', borderRadius: '0.375rem' }}
                    labelStyle={{ color: '#F9FAFB' }}
                  />
                  <Legend 
                    formatter={(value) => <span className="text-gray-300">{value}</span>}
                  />
                  <Area type="monotone" dataKey="visitors" stroke="#4F46E5" fill="#4F46E5" fillOpacity={0.2} name="Unique Visitors" />
                  <Area type="monotone" dataKey="pageViews" stroke="#7C3AED" fill="#7C3AED" fillOpacity={0.2} name="Page Views" />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Most Visited Pages with Progress Bars */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Most Visited Pages</h3>
              <div className="text-sm text-gray-400">
                {analyticsData.most_visited_pages.length} pages
              </div>
            </div>
            <div className="space-y-4">
              {processMostVisitedPages().map((page, index) => (
                <div key={index} className="group">
                  <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-indigo-900/30 rounded-lg flex items-center justify-center mr-3">
                        <Activity className="h-4 w-4 text-indigo-400" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white group-hover:text-indigo-400 transition-colors">
                          {page.truncatedUrl}
                        </div>
                        <div className="text-xs text-gray-500">
                          {page.visits} visits
                        </div>
                      </div>
                    </div>
                    <div className="text-sm font-medium text-white">
                      {(page.visits / analyticsData.unique_visitors * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-indigo-500 h-2 rounded-full" 
                      style={{ width: `${page.fillPercent}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Devices & Countries Side by Side */}
          <div className="space-y-6">
            {/* Devices Chart */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-lg">
              <h3 className="text-lg font-medium mb-4">Device Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analyticsData.devices}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                      nameKey="type"
                      label={({ type, percent }) => `${type}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {analyticsData.devices.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={colors[entry.type] || colors.other} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', borderRadius: '0.375rem' }}
                      itemStyle={{ color: '#F9FAFB' }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36} 
                      formatter={(value) => <span className="text-gray-300">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Countries Chart */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-lg">
              <h3 className="text-lg font-medium mb-4">Visitor Countries</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analyticsData.countries}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                      nameKey="code"
                      label={({ code, percent }) => `${code}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {analyticsData.countries.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={colors[entry.code] || colors.other} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', borderRadius: '0.375rem' }}
                      itemStyle={{ color: '#F9FAFB' }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36} 
                      formatter={(value) => <span className="text-gray-300">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
        
        {/* Activity Table Section */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow-lg mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
            <h3 className="text-lg font-medium mb-2 md:mb-0">Recent Activity</h3>
            
            <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-4 w-full md:w-auto">
              {/* Search Input */}
              <div className="relative w-full md:w-64">
                <input
                  type="text"
                  placeholder="Search activities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
              
              {/* Items per page selector */}
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          </div>
          
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700/50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Time</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Page</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Device</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Location</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Stats</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700 bg-gray-800">
                {currentItems.length > 0 ? (
                  currentItems.map((activity, index) => (
                    <tr key={index} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="text-gray-300">
                          {new Date(activity.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {new Date(activity.timestamp).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-white truncate max-w-xs">{activity.page_title}</div>
                        <div className="text-indigo-400 text-xs truncate max-w-xs">{activity.page_url}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {activity.user.username ? (
                          <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-2 ${
                              activity.user.role === 'superuser' ? 'bg-amber-500' :
                              activity.user.role === 'admin' ? 'bg-emerald-500' :
                              'bg-blue-500'
                            }`}></div>
                            <div>
                              <div className="text-white">{activity.user.username}</div>
                              <div className="text-gray-500 text-xs capitalize">{activity.user.role}</div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <div className="w-2 h-2 rounded-full mr-2 bg-gray-500"></div>
                            <span className="text-gray-500">Guest</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="flex items-center">
                          {activity.device.device_type === 'desktop' ? (
                            <Monitor className="h-4 w-4 text-blue-400 mr-2" />
                          ) : activity.device.device_type === 'mobile' ? (
                            <Smartphone className="h-4 w-4 text-purple-400 mr-2" />
                          ) : (
                            <Settings className="h-4 w-4 text-gray-400 mr-2" />
                          )}
                          <div>
                            <div className="text-white">{activity.device.browser} {activity.device.browser_version}</div>
                            <div className="text-gray-500 text-xs">{activity.device.os}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="flex items-center">
                          <Globe className="h-4 w-4 text-green-400 mr-2" />
                          <div>
                            <div className="text-white">{activity.location.country_code}</div>
                            <div className="text-gray-500 text-xs">{activity.location.city}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="text-white">{activity.time_on_page.toFixed(1)}s</div>
                        <div className="text-gray-500 text-xs">{activity.interactions} interactions</div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      No activities found matching your search
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {filteredActivity.length > 0 && (
            <div className="flex items-center justify-between border-t border-gray-700 px-4 py-3 sm:px-6 mt-4">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
                    currentPage === 1 
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                      : 'bg-gray-700 text-white hover:bg-gray-600'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`relative ml-3 inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
                    currentPage === totalPages 
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                      : 'bg-gray-700 text-white hover:bg-gray-600'
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-400">
                    Showing <span className="font-medium text-white">{indexOfFirstItem + 1}</span> to{' '}
                    <span className="font-medium text-white">
                      {Math.min(indexOfLastItem, filteredActivity.length)}
                    </span>{' '}
                    of <span className="font-medium text-white">{filteredActivity.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                        currentPage === 1 
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                      }`}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    
                    {/* Page Numbers */}
                    {[...Array(totalPages).keys()].map(number => (
                      <button
                        key={number + 1}
                        onClick={() => setCurrentPage(number + 1)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                          currentPage === number + 1
                            ? 'z-10 bg-indigo-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {number + 1}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                        currentPage === totalPages 
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                      }`}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-700 py-4">
        <div className="container mx-auto px-4 text-center text-gray-400 text-sm">
          Analytics Dashboard • Last updated: {new Date().toLocaleString()} • v1.2.0
        </div>
      </footer>
    </div>
  );
};

export default AnalyticsDashboard;